<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { AnalysisWizardModel } from '../../models/AnalysisWizardModel'
  import { FileCheck, FileQuestion } from 'lucide-vue-next'
  import FullScreenModal from '@/components/FullScreenModal.vue'
  import DataViewer from '@/components/DataViewer.vue'

  export interface IInputDataStepStatusProps {
    wizardModel: AnalysisWizardModel
  }

  const props = defineProps<IInputDataStepStatusProps>()

  const dataViewerModalOpen = ref<boolean>(false)

  const viewFileData = async () => {
    if (props.wizardModel.sharedModel?.inputFile != null) {
      dataViewerModalOpen.value = true
    }
  }

  const closeDataViewerModal = () => {
    dataViewerModalOpen.value = false
  }

  const hasSelectedFile = computed(() => props.wizardModel.sharedModel?.inputFile != null)

  const selectedFileKey = computed(
    () =>
      props.wizardModel.sharedModel?.inputFile?.id ??
      props.wizardModel.sharedModel?.inputFile?.name ??
      null
  )

  // animation trigger for when a file becomes selected
  const animateSelected = ref(false)
  const ANIMATION_DURATION = 900

  watch(
    selectedFileKey,
    (newVal, oldVal) => {
      if (oldVal === undefined) return // skip initial mount
      if (newVal !== oldVal) {
        animateSelected.value = false
        Promise.resolve().then(() => {
          animateSelected.value = true
          setTimeout(() => {
            animateSelected.value = false
          }, ANIMATION_DURATION)
        })
      }
    },
    { deep: false }
  )
</script>

<template>
  <a-tooltip>
    <template #title>
      {{ hasSelectedFile ? 'Preview  Data' : 'Select file to preview' }}
    </template>
    <div @click="viewFileData" class="flex" :class="{ 'cursor-pointer': hasSelectedFile }">
      <component
        :is="hasSelectedFile ? FileCheck : FileQuestion"
        class="inline size-6"
        :class="{ 'status-animate': animateSelected }"
        :style="{
          color: hasSelectedFile ? 'var(--primary, #5aa745)' : 'var(--foreground-80, #9CA3AF)'
        }"
        aria-hidden="true"
      />
      <span class="ml-2 self-center truncate">
        <b>Selected dataset:</b>
        <span class="ml-1">
          {{
            hasSelectedFile
              ? props.wizardModel.sharedModel?.inputFile?.name
              : 'No data set selected'
          }}
        </span>
      </span>
    </div>
  </a-tooltip>

  <!-- Data Viewer Modal -->
  <FullScreenModal
    :open="dataViewerModalOpen"
    title="Data Viewer"
    :description="props.wizardModel.sharedModel?.inputFile?.name"
    @close="closeDataViewerModal"
  >
    <DataViewer :fileId="props.wizardModel.sharedModel?.inputFile?.id || ''" />
  </FullScreenModal>
</template>

<style scoped>
  @keyframes statusPulse {
    0% {
      opacity: 0;
      transform: scale(1);
    }
    10% {
      opacity: 1;
    }
    35% {
      transform: scale(1.15);
    }
    55% {
      transform: scale(0.95);
    }
    75% {
      transform: scale(1.08);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  .status-animate {
    animation: statusPulse 900ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
    will-change: transform, opacity;
  }
</style>
